import { EngagementRef } from "PFCore/types";
import { useMemo } from "react";

export type EngagementRowSpan = {
  getRowSpan: (rowIndex: number) => number;
  isRowSpanHidden: (rowIndex: number) => boolean;
};

const groupConsecutiveByEngagement = (
  engagements: Array<EngagementRef | undefined>
): Array<{ start: number; span: number }> => {
  const groups: Array<{ start: number; span: number }> = [];

  engagements.forEach((engagement, index) => {
    const lastGroup = groups[groups.length - 1];
    const lastEngagement = lastGroup ? engagements[lastGroup.start] : undefined;
    const isSameEngagement = engagement !== undefined && engagement.id === lastEngagement?.id;

    if (isSameEngagement) {
      lastGroup.span++;
    } else {
      groups.push({ start: index, span: 1 });
    }
  });

  return groups;
};

export const useCalculateEngagementRowSpan = (
  engagements: Array<EngagementRef | undefined>
): EngagementRowSpan => {
  const engagementKey = engagements.map((engagement) => engagement?.id ?? "").join("\0");

  return useMemo(() => {
    const groups = groupConsecutiveByEngagement(engagements);

    const rowSpanMap = new Map(groups.map(({ start, span }) => [start, span]));
    const hiddenRows = new Set(
      groups.flatMap(({ start, span }) => Array.from({ length: span - 1 }, (_, offset) => start + offset + 1))
    );

    return {
      getRowSpan: (rowIndex: number) => rowSpanMap.get(rowIndex) ?? 1,
      isRowSpanHidden: (rowIndex: number) => hiddenRows.has(rowIndex)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementKey]);
};
